'use client'

import Link from 'next/link'
import { formatTimestamp } from '@/lib/timed-ui/formatting'
import Badge from '../common/Badge'

interface LocationDateRange {
  city: string
  earliest: Date
  latest: Date
}

interface SeriesData {
  id: string
  baseName: string
  count: number
  eventType?: string
  venue?: string | { name?: string; venue?: string }
  venueCity?: string
  venueState?: string
  earliestDate: Date | null
  latestDate: Date | null
  locationDateRanges?: LocationDateRange[]
  slug?: string
}

interface SeriesListItemProps {
  series: SeriesData
  seatedSeriesCalendar?: boolean
}

const SeriesListItem = ({ series, seatedSeriesCalendar }: SeriesListItemProps) => {
  const href = seatedSeriesCalendar && series.eventType === 'RESERVED_SEATING'
    ? `/events/series/${encodeURIComponent(series.baseName)}`
    : `/?q=${encodeURIComponent(series.baseName)}`

  const locations = series.locationDateRanges || []
  const MAX_LOCATIONS_SHOWN = 2
  const visibleLocations = locations.slice(0, MAX_LOCATIONS_SHOWN)
  const hiddenCount = Math.max(0, locations.length - MAX_LOCATIONS_SHOWN)

  const dateRangeText = locations.length === 0 && series.earliestDate && series.latestDate
    ? `${formatTimestamp(series.earliestDate.getTime() / 1000, 'MMM dd')} - ${formatTimestamp(series.latestDate.getTime() / 1000, 'MMM dd, yyyy')}`
    : ''

  const venueName = typeof series.venue === 'string'
    ? series.venue
    : series.venue?.name || series.venue?.venue || ''

  const formatLocationRange = (loc: LocationDateRange) => {
    const from = formatTimestamp(loc.earliest.getTime() / 1000, 'MMM dd')
    const to = formatTimestamp(loc.latest.getTime() / 1000, 'MMM dd')
    if (from === to) return from
    return `${from} - ${to}`
  }

  return (
    <Link
      href={href}
      className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-3.5 bg-surface border border-surface-border rounded-xl cursor-pointer hover:border-primary/30 hover:shadow-sm active:bg-surface-secondary transition-all duration-150 animate-fade-in touch-manipulation"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-text-primary text-sm truncate">
            {series.baseName}
          </h3>
          <Badge variant="primary">{series.count} dates</Badge>
        </div>
        {visibleLocations.length > 0 ? (
          <div className="mt-1 space-y-0.5">
            {visibleLocations.map((loc) => (
              <div key={loc.city} className="flex items-center gap-1.5 text-xs text-text-tertiary">
                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <span className="truncate">
                  <span className="font-medium text-text-secondary">{loc.city}</span>
                  {' '}{formatLocationRange(loc)}
                </span>
              </div>
            ))}
            {hiddenCount > 0 && (
              <div className="text-xs text-text-tertiary pl-4.5 font-medium">
                +{hiddenCount} more {hiddenCount === 1 ? 'city' : 'cities'}
              </div>
            )}
          </div>
        ) : (
          <>
            {venueName && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <svg className="w-3 h-3 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <span className="text-xs text-text-tertiary truncate">{venueName}</span>
              </div>
            )}
            {(series.venueCity || series.venueState) && (
              <span className="sm:hidden text-xs text-text-tertiary mt-0.5 block truncate">
                {[series.venueCity, series.venueState].filter(Boolean).join(', ')}
              </span>
            )}
          </>
        )}
      </div>

      {visibleLocations.length === 0 && (
        <>
          <div className="hidden sm:block text-xs text-text-tertiary truncate w-36 text-center">
            {series.venueCity && series.venueState
              ? `${series.venueCity}, ${series.venueState}`
              : ''}
          </div>
          <div className="flex-shrink-0 text-right">
            {dateRangeText && (
              <div className="text-xs sm:text-sm font-medium text-text-primary whitespace-nowrap">
                {dateRangeText}
              </div>
            )}
          </div>
        </>
      )}

      {visibleLocations.length > 0 && (
        <div className="hidden sm:block flex-shrink-0 text-right">
          <div className="text-xs font-medium text-text-secondary whitespace-nowrap">
            {locations.length} {locations.length === 1 ? 'city' : 'cities'}
          </div>
        </div>
      )}

      <svg className="w-4 h-4 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  )
}

export default SeriesListItem
